import { Seat2, type SeatOptions } from "./seat";
import Konva from "konva";
import { v4 as uuidv4 } from "uuid";
import { GenericNodeEvents } from "./common";
import type { LoadRowStateParam, Position, RowState2 } from "./types";

const DEFAULT_SEAT_WIDTH = 10;
const DEFAULT_SEAT_HEIGHT = 10;
const DEFAULT_SEATS_GAP = 10;
const PADDING = 5;
const LABEL_FONT_SIZE = 15;
const MIN_LABEL_WIDTH = 18;

export const ROW_CONTAINER_INITIAL_WIDTH = 100;
export const ROW_CONTAINER_INITIAL_HEIGHT = 10;

export type RowOptions = {
  blockGroup: Konva.Group;
  seatsOptions: Omit<SeatOptions, "rowGroup">;
  onWidthChange: (newWidth: number) => void;
  label: string;
  x?: number;
  y?: number;
};

export class Row2 extends GenericNodeEvents<
  Row2,
  LoadRowStateParam & { position: Position; label: string }
> {
  private id!: string;
  private readonly blockGroup: Konva.Group;
  private readonly seatsGroup: Konva.Group;
  private container: Konva.Rect;
  private labelLeft: Konva.Text;
  private labelRight: Konva.Text;
  public readonly seats: Seat2[];
  private state: LoadRowStateParam & { position: Position; label: string };

  get nodeId() {
    return this.id;
  }

  get currentState() {
    return this.state;
  }

  constructor(
    blockGroup: Konva.Group,
    state: LoadRowStateParam & { position: Position; label: string },
  ) {
    const group = new Konva.Group();

    super(group, "row");
    this.state = structuredClone(state);

    const container = new Konva.Rect({
      width: ROW_CONTAINER_INITIAL_WIDTH,
      height: ROW_CONTAINER_INITIAL_HEIGHT,
    });
    this.blockGroup = blockGroup;
    this.container = container;
    this.seatsGroup = group;
    this.seats = [];

    this.labelLeft = new Konva.Text();
    this.labelRight = new Konva.Text();

    this.blockGroup.add(this.seatsGroup);

    this.target = this;
  }

  public moveToBottom() {
    this.seatsGroup.moveToBottom();
  }

  public moveToTop() {
    this.seatsGroup.moveToTop();
  }

  create() {
    this.seatsGroup.destroyChildren();
    while (this.seats.length) {
      this.seats.pop();
    }

    this.initElements();

    this.seatsGroup.add(this.container);
    this.seatsGroup.add(this.labelLeft);
    this.seatsGroup.add(this.labelRight);

    this.state.id = this.state.id || uuidv4();
    this.id = this.state.id;

    this.container.setAttrs({
      ...this.state.position,
      strokeWidth: this.state.borderWidth ?? 1,
      stroke: "#0a3653",
      fill: this.state.fill,
    });

    // A diagonal (seatStair) row's container is a full-width horizontal strip
    // that doesn't match the seats' diagonal shape. Since the next row's group
    // stacks above this one, that strip would steal clicks from seats of the
    // row above that dip into its band. It's an invisible artifact, so keep it
    // out of the hit graph.
    this.container.listening(!this.state.seatStair);

    this.state.seats.forEach((state) => {
      const pos = this.getNewSeatPosition(this.seats.length);
      const rowNumber = this.getNewRowNumber(this.seats.length);
      state.id = state.id || uuidv4();
      state.position = pos;
      state.width = state.width || DEFAULT_SEAT_WIDTH;
      state.height = state.height || DEFAULT_SEAT_HEIGHT;
      state.label = state.label || rowNumber;
      state.strokeWidth = this.state.seatsBorderWidth;
      state.fill = this.state.seatsBackground;
      state.fillText = this.state.seatsTextColor;

      const dir = this.state.direction;
      let seatNumber =
        dir === "reverse"
          ? this.state.seats.length - +rowNumber + 1
          : +rowNumber;

      const startFrom = this.state.fromNumberStart
        ? this.state.fromNumberStart - 1
        : 0;

      if (dir === "reverse") {
        seatNumber += startFrom;
      } else {
        seatNumber -= startFrom;
      }

      const seat = new Seat2(seatNumber, this.seatsGroup, state, this);
      seat.create();

      if (this.onPressCallback) {
        seat.onPress(this.onPressCallback);
      }

      this.seats.unshift(seat);
    });

    this.loadLabel(this.state.label);

    if (this.onStateChangeCallback) {
      this.onStateChangeCallback(this.state);
    }

    this.setContainerMaxContent();
  }

  update(newState: RowState2) {
    this.state = newState as any;
    this.id = this.state.id || uuidv4();
    this.container.setAttrs({
      ...this.state.position,
      strokeWidth: this.state.borderWidth,
    });

    this.state.seats.forEach((state, arrIndex) => {
      const seatIndex = this.seats.findIndex(
        (seat) => seat.nodeId === state.id,
      );
      if (seatIndex < 0) {
        console.error(
          "UNABLE TO FIND SEAT: ",
          state,
          this.seats.map((seat) => seat.nodeId),
        );
      }

      const seat = this.seats[seatIndex];
      // `this.seats` is unshift-reversed, so `seatIndex` reverses the layout.
      // A staircase row must position by its natural array index so that
      // seat 1 (index 0) renders at the inner top and stays aligned with its
      // seatNumber/label — which the bitmap/courtesy/picker all key on.
      const posIndex = this.state.seatStair ? arrIndex : seatIndex;
      const pos = this.getNewSeatPosition(posIndex);

      state.position = pos;
      state.width = state.width || DEFAULT_SEAT_WIDTH;
      state.height = state.height || DEFAULT_SEAT_HEIGHT;
      seat.update();
    });

    this.loadLabel(this.state.label);
  }

  private getNewRowNumber(index: number): string {
    const start = this.state.fromNumberStart
      ? this.state.fromNumberStart - 1
      : 0;
    if (this.state.direction === "normal") {
      return (start + index + 1).toString();
    }

    return (this.state.seats.length - index + start).toString();
  }

  private getNewSeatPosition(index: number): Position {
    const y = this.container.y() + this.container.height() / 2;

    const gap = this.state.seatsGap || DEFAULT_SEATS_GAP;
    let labelWidth = (this.labelLeft.width() || DEFAULT_SEAT_WIDTH) + gap;
    if (this.state.hideLabelLeft) {
      labelWidth = 0;
    }

    // Diagonal staircase row: each seat steps by (dx, dy) from the first.
    const stair = this.state.seatStair;
    if (stair) {
      return {
        x: this.container.x() + PADDING + labelWidth + index * stair.dx,
        y: y + index * stair.dy,
      };
    }

    let x =
      this.container.x() +
      (index * (DEFAULT_SEAT_WIDTH + gap) + labelWidth + PADDING);
    return {
      x,
      y,
    };
  }

  private setContainerMaxContent() {
    const gap = this.state.seatsGap || DEFAULT_SEATS_GAP;
    let labelWidth = (this.labelLeft.width() || DEFAULT_SEAT_WIDTH) + gap;

    if (this.state.hideLabelRight) {
      labelWidth = (labelWidth - gap) / 2;
    }

    if (this.state.hideLabelLeft) {
      labelWidth = (labelWidth - gap) / 2;
    }

    const newWidth =
      labelWidth * 2 +
      PADDING * 2 +
      this.state.seats.length * (DEFAULT_SEAT_WIDTH + gap);

    this.state.width = newWidth;
    this.container.setAttr("width", newWidth);
    this.loadLabel(this.state.label);
  }

  private initElements() {
    this.container = new Konva.Rect({
      height: ROW_CONTAINER_INITIAL_HEIGHT + 2 * PADDING,
      strokeWidth: this.state.borderWidth,
    });

    this.labelLeft = new Konva.Text();
    this.labelRight = new Konva.Text();
  }

  private loadLabel(label: string) {
    const baseX = this.container.x();

    const baseY = this.container.y() + this.container.height() / 2;

    if (!this.state.hideLabelRight) {
      this.labelRight.setAttrs({
        text: label,
        x: baseX + this.container.width() - PADDING,
        fontFamily: "Geist Mono",
        fontStyle: "bold",
        fontSize: LABEL_FONT_SIZE,
        fill: this.state.labelColor || "black",
        rotation: this.state.rotationText,
        y: baseY,
        align: "center",
        verticalAlign: "center",
      });
    }

    if (!this.state.hideLabelLeft) {
      this.labelLeft.setAttrs({
        text: label,
        fontFamily: "Geist Mono",
        rotation: this.state.rotationText,
        fontStyle: "bold",
        fill: this.state.labelColor || "black",
        fontSize: LABEL_FONT_SIZE,
        x: baseX + PADDING,
        y: baseY,
      });
    }

    this.labelLeft.width(Math.max(MIN_LABEL_WIDTH, this.labelLeft.width()));
    this.labelRight.width(Math.max(MIN_LABEL_WIDTH, this.labelRight.width()));

    this.labelRight.offsetY(this.labelRight.height() / 2);
    this.labelRight.offsetX(this.labelRight.width());
    this.labelLeft.offsetY(this.labelLeft.height() / 2);
  }
}
